/**
 * Global Telegram Back Button Manager
 * Automatically configures the Telegram back button based on current screen and app state
 */

/* eslint-disable no-console */

import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTelegramWebApp } from '../providers/TelegramWebAppProvider';
import { RootState } from '../store/store';
import { selectActiveVisit } from '../store/slices/visitsSlice';
import { navigateTo, goBack } from '../store/slices/navigationSlice';
import { Screen } from '../store/slices/navigationSlice';
import { 
  handleModalBack, 
  shouldCloseModalOnBack, 
  getModalHierarchy,
  getTopModal,
  hasAnyOpenModal,
  findTopModalWithCloseButton,
  tryCloseModalWithButton
} from '../hooks/useGlobalModal';

export const TelegramBackButtonManager: React.FC = () => {
  const dispatch = useDispatch();
  const { configureBackButton, showHapticFeedback } = useTelegramWebApp();
  const currentVisit = useSelector(selectActiveVisit);
  const navigation = useSelector((state: RootState) => state.navigation);

  // Smart back navigation logic with modal-first priority
  const getBackAction = useCallback((): (() => void) => {
    const { currentScreen, previousScreen, navigationHistory } = navigation;

    console.log('🔙 Determining back action for screen:', currentScreen, {
      previousScreen,
      hasActiveVisit: !!currentVisit,
      navigationHistory: navigationHistory.slice(-3), // Show last 3 screens
      modalStack: getModalHierarchy()
    });

    // PRIORITY 1: Handle ANY open modal first (global + DOM detection)
    if (hasAnyOpenModal()) {
      console.log(`🔙 MODAL-FIRST: Detected open modal(s), attempting to close first`);
      
      // Try global modal system first
      if (shouldCloseModalOnBack()) {
        const topModalId = getTopModal();
        console.log(`🔙 GLOBAL MODAL: Handling back for top modal: ${topModalId}`);
        
        return () => {
          const modalHandled = handleModalBack();
          if (modalHandled) {
            showHapticFeedback('impact', 'light');
            console.log(`✅ Global modal ${topModalId} handled back navigation`);
          }
        };
      }
      
      // Fallback to DOM modal detection with close button priority
      const topModalWithClose = findTopModalWithCloseButton();
      if (topModalWithClose) {
        console.log(`🔙 DOM MODAL: Found modal with close button: ${topModalWithClose.modalId}`);
        console.log(`🔙 DOM MODAL: Close buttons found: ${topModalWithClose.closeButtons.length}`);
        
        return () => {
          const modalClosed = tryCloseModalWithButton(topModalWithClose);
          if (modalClosed) {
            showHapticFeedback('impact', 'light');
            console.log(`✅ DOM modal ${topModalWithClose.modalId} closed via back button`);
          } else {
            console.log(`⚠️ DOM modal ${topModalWithClose.modalId} could not be closed via button`);
            // Try global modal as fallback
            const globalHandled = handleModalBack();
            if (globalHandled) {
              showHapticFeedback('impact', 'light');
              console.log(`✅ Fallback: Global modal handled back navigation`);
            }
          }
        };
      }
      
      // If modal detected but no close button found, try generic close
      console.log(`🔙 GENERIC MODAL: Modal detected but no close button, trying generic close`);
      return () => {
        // Try pressing Escape key as fallback
        const escapeEvent = new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true
        });
        document.dispatchEvent(escapeEvent);
        
        // Also try clicking outside the modal
        const topModalWithClose = findTopModalWithCloseButton();
        if (topModalWithClose) {
          const backdrop = topModalWithClose.element.closest('[class*="fixed"][class*="inset-0"]') as HTMLElement;
          if (backdrop) {
            backdrop.click();
          }
        }
        
        showHapticFeedback('impact', 'light');
        console.log(`🔙 Attempted generic modal close (Escape + backdrop click)`);
      };
    }

    // PRIORITY 2: Handle screen navigation
    switch (currentScreen) {
      case 'evidenceCapture':
        return () => {
          if (currentVisit) {
            dispatch(navigateTo('visitActive'));
          } else {
            dispatch(navigateTo('dashboard'));
          }
        };

      case 'visitActive':
        return () => {
          // Check navigation history for proper back stack handling
          // If we have a rich history, use it intelligently
          if (navigationHistory.length >= 2) {
            const screenBeforeLast = navigationHistory[navigationHistory.length - 2];
            console.log(`🔙 NAVIGATION STACK: Going back to ${screenBeforeLast} (history: ${navigationHistory.slice(-3).join(' → ')})`);
            
            // Validate the screen before navigating
            const validScreens: Screen[] = ['dashboard', 'interactions', 'customerSelect', 'visitActive', 'evidenceCapture'];
            if (validScreens.includes(screenBeforeLast)) {
              return dispatch(navigateTo(screenBeforeLast));
            }
          }
          
          // Fallback to previous screen logic
          if (previousScreen === 'customerSelect') {
            return dispatch(navigateTo('customerSelect'));
          } else if (previousScreen === 'interactions') {
            return dispatch(navigateTo('interactions'));
          } else {
            return dispatch(navigateTo('dashboard'));
          }
        };

      case 'customerSelect':
        return () => {
          if (navigationHistory.length >= 2) {
            const screenBeforeLast = navigationHistory[navigationHistory.length - 2];
            console.log(`🔙 NAVIGATION STACK: Going back to ${screenBeforeLast} from customerSelect`);
            
            const validScreens: Screen[] = ['dashboard', 'interactions'];
            if (validScreens.includes(screenBeforeLast)) {
              return dispatch(navigateTo(screenBeforeLast));
            }
          }
          
          if (previousScreen === 'interactions') {
            return dispatch(navigateTo('interactions'));
          } else {
            return dispatch(navigateTo('dashboard'));
          }
        };

      case 'interactions':
        return () => {
          if (navigationHistory.length >= 2) {
            const screenBeforeLast = navigationHistory[navigationHistory.length - 2];
            console.log(`🔙 NAVIGATION STACK: Going back to ${screenBeforeLast} from interactions`);
            
            const validScreens: Screen[] = ['dashboard'];
            if (validScreens.includes(screenBeforeLast)) {
              return dispatch(navigateTo(screenBeforeLast));
            }
          }
          
          return dispatch(navigateTo('dashboard'));
        };

      case 'visitSummary':
        return () => {
          if (currentVisit) {
            dispatch(navigateTo('visitActive'));
          } else {
            dispatch(navigateTo('dashboard'));
          }
        };

      case 'profile':
      case 'reports':
      case 'products':
      case 'Phone':
        return () => {
          if (navigationHistory.length >= 2) {
            const screenBeforeLast = navigationHistory[navigationHistory.length - 2];
            console.log(`🔙 NAVIGATION STACK: Going back to ${screenBeforeLast} from ${currentScreen}`);
            
            // Validate screen before navigation
            const validScreens: Screen[] = ['dashboard', 'interactions', 'customerSelect', 'visitActive'];
            if (validScreens.includes(screenBeforeLast)) {
              return dispatch(navigateTo(screenBeforeLast));
            }
          }
          return dispatch(goBack());
        };

      case 'dashboard':
        // On dashboard, back button could close the app or show confirmation
        return () => {
          console.log('🏠 Dashboard back button - would close app or show exit confirmation');
          // Optionally show confirmation dialog
          // Or let Telegram handle the back button (hide it)
        };

      case 'auth':
      case 'onboarding':
        // Don't show back button on auth/onboarding
        return () => {};

      default:
        return () => {
          console.log(`🔙 DEFAULT: Using goBack for screen ${currentScreen}`);
          dispatch(goBack());
        };
    }
  }, [navigation, currentVisit, dispatch, showHapticFeedback]);

  // Handle back button click
  const handleBackButton = useCallback(() => {
    // Execute the appropriate back action
    const backAction = getBackAction();
    backAction();
  }, [getBackAction]);

  // Configure Telegram back button based on current screen and modal state
  useEffect(() => {
    const { currentScreen } = navigation;

    // Screens where back button should be hidden (unless there's a modal)
    const hideBackButtonScreens: Screen[] = ['dashboard', 'auth', 'onboarding'];
    
    // Check if there are active modals (enhanced detection)
    const hasActiveModals = hasAnyOpenModal();

    // Determine if back button should be visible
    const shouldShowBackButton = !hideBackButtonScreens.includes(currentScreen) || hasActiveModals;

    if (shouldShowBackButton) {
      // Show and configure back button
      configureBackButton(true, handleBackButton);
      
      const topModal = getTopModal();
      const topModalWithClose = findTopModalWithCloseButton();
      
      if (topModal) {
        console.log(`🔙 Back button configured for GLOBAL modal: ${topModal} (screen: ${currentScreen})`);
      } else if (topModalWithClose) {
        console.log(`🔙 Back button configured for DOM modal: ${topModalWithClose.modalId} (type: ${topModalWithClose.type}, close buttons: ${topModalWithClose.closeButtons.length})`);
      } else if (hasActiveModals) {
        console.log(`🔙 Back button configured for detected modal (screen: ${currentScreen})`);
      } else {
        console.log(`🔙 Back button configured for screen: ${currentScreen}`);
      }
    } else {
      // Hide back button
      configureBackButton(false);
      console.log('🔙 Back button hidden for screen:', currentScreen);
    }
  }, [navigation.currentScreen, handleBackButton, configureBackButton]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      configureBackButton(false);
      console.log('🔙 Back button cleaned up');
    };
  }, [configureBackButton]);

  // This component doesn't render anything visible
  return null;
};

/**
 * Hook to get current back button configuration
 */
export const useBackButtonConfig = () => {
  const navigation = useSelector((state: RootState) => state.navigation);
  const currentVisit = useSelector(selectActiveVisit);

  const shouldShowBackButton = !['dashboard', 'auth', 'onboarding'].includes(navigation.currentScreen);
  const backAction = getBackActionText(navigation.currentScreen, navigation.previousScreen, !!currentVisit);

  return {
    isVisible: shouldShowBackButton,
    backAction,
    currentScreen: navigation.currentScreen,
    previousScreen: navigation.previousScreen
  };
};

/**
 * Helper function to get human-readable back action text
 */
function getBackActionText(
  currentScreen: Screen, 
  previousScreen: Screen | null, 
  hasActiveVisit: boolean
): string {
  switch (currentScreen) {
    case 'evidenceCapture':
      return hasActiveVisit ? 'Back to Visit' : 'Back to Dashboard';
    case 'visitActive':
      if (previousScreen === 'customerSelect') return 'Back to Customer Selection';
      if (previousScreen === 'interactions') return 'Back to Interactions';
      return 'Back to Dashboard';
    case 'customerSelect':
      if (previousScreen === 'interactions') return 'Back to Interactions';
      return 'Back to Dashboard';
    case 'interactions':
      return 'Back to Dashboard';
    case 'visitSummary':
      return hasActiveVisit ? 'Back to Visit' : 'Back to Dashboard';
    case 'profile':
    case 'reports':
    case 'products':
      return 'Back';
    default:
      return 'Back';
  }
}
